import type { ReactNode } from "react";
import { ArrowRight, Check, CircleAlert, FileText, Link2, RotateCcw, X } from "lucide-react";
import type { ConfirmationPreview, MessageCard } from "../api/contract";
import type { ConversationController } from "../hooks/useConversation";
import { Spinner } from "./Spinner";
import { buyerDataClassName } from "../lib/buyerPrivacy";

interface ConfirmationCardProps {
  chat: ConversationController;
}

const POST_WORKING: Record<Extract<ConfirmationPreview, { kind: "post" }>["action"], string> = {
  publish: "Publishing…", schedule: "Scheduling…", update: "Updating…", unpublish: "Unpublishing…", delete: "Deleting…",
};

/** The present-tense word on the button that was pressed while its answer is on its way. */
function workingLabel(preview: ConfirmationPreview | undefined): string {
  if (preview?.kind === "delete") return "Deleting…";
  if (preview?.kind === "photos") return "Adding…";
  if (preview?.kind === "post") return POST_WORKING[preview.action];
  return "Working…";
}

/** The page shown in an address row: the host and the path, as the owner would type it. */
function address(host: string | undefined, path: string): string {
  return `${host ?? ""}${path}`;
}

function Preview({ preview }: { preview: ConfirmationPreview }): ReactNode {
  switch (preview.kind) {
    case "order":
      return <><div className="bw-confirm-record" data-staying><span className="bw-confirm-record-icon"><FileText size={18} aria-hidden="true" /></span><span className="bw-confirm-record-text"><strong>Order #{preview.number}</strong>{preview.createdAt && <time dateTime={preview.createdAt}>{new Date(preview.createdAt).toLocaleDateString()}</time>}<span>{preview.amount}</span>{preview.items.map((item, index) => <span key={index}>{item}</span>)}</span></div><ul className="bw-confirm-parts bw-confirm-order-effects">{preview.consequences.map((text, index) => <li key={index}>{text}</li>)}</ul></>;
    case "shop":
      return <div className="bw-confirm-record" data-staying={!preview.action.endsWith("delete") || undefined}><span className="bw-confirm-record-icon"><FileText size={18} aria-hidden="true" /></span><span className="bw-confirm-record-text"><strong>{preview.name}</strong><span>{preview.detail}</span></span></div>;
    case "delete":
      return (
        <>
          <div className="bw-confirm-record">
            <span className="bw-confirm-record-icon"><Link2 size={18} aria-hidden="true" /></span>
            <span className="bw-confirm-record-text">
              <strong>{preview.label}</strong>
              {preview.subtitle && <span>{preview.subtitle}</span>}
            </span>
          </div>
          {preview.pages.length > 0 && (
            <div className="bw-confirm-pages">
              <span>Comes off</span>
              {preview.pages.map((page) => <span key={page} className="bw-confirm-chip">{page}</span>)}
            </div>
          )}
        </>
      );
    case "restore":
      // There are no thumbnails of the earlier design, so the two states are named rather than drawn.
      return preview.part === "design" ? (
        <>
          <div className="bw-confirm-versions">
            <figure><div className="bw-confirm-version"><span>Your design</span></div><figcaption>Now</figcaption></figure>
            <ArrowRight size={18} aria-hidden="true" />
            <figure><div className="bw-confirm-version" data-earlier><span>Your design</span></div><figcaption><strong>Before {preview.before}</strong></figcaption></figure>
          </div>
          <p className="bw-confirm-aside">Only the look changes</p>
        </>
      ) : (
        <ul className="bw-confirm-parts">
          {["Design", "Text", "Photos"].map((part) => (
            <li key={part}><RotateCcw size={16} aria-hidden="true" /><span>{part}</span><span>Goes back</span></li>
          ))}
        </ul>
      );
    case "icon":
      return (
        <div className="bw-confirm-tabbar" role="img" aria-label={`A browser tab showing your ${preview.label} beside ${preview.title}`}>
          <div className="bw-confirm-tab">
            {preview.src ? <img src={preview.src} alt="" /> : <span className="bw-confirm-tab-fallback">{preview.label.slice(0, 2)}</span>}
            <span>{preview.title}</span>
            <X size={12} aria-hidden="true" />
          </div>
        </div>
      );
    case "rename":
      return (
        <div className="bw-confirm-addresses">
          <div><span data-old>{address(preview.host, preview.from)}</span><strong>Stops working</strong></div>
          <div><span data-new>{address(preview.host, preview.to)}</span><span>New address</span></div>
        </div>
      );
    case "post": {
      const leaving = preview.action === "unpublish" || preview.action === "delete";
      return (
        <>
          <div className="bw-confirm-record" data-staying={leaving ? undefined : true}>
            <span className="bw-confirm-record-icon"><FileText size={18} aria-hidden="true" /></span>
            <span className="bw-confirm-record-text">
              <strong>{preview.title}</strong>
              <span>{preview.address}</span>
              {preview.when && <span>Goes live {preview.when}</span>}
            </span>
          </div>
          {preview.shows.length > 0 && (
            <div className="bw-confirm-pages">
              <span>{leaving ? "Comes off" : "Shows on"}</span>
              {preview.shows.map((page) => <span key={page} className="bw-confirm-chip">{page}</span>)}
            </div>
          )}
        </>
      );
    }
    case "photos":
      return (
        <ul className="bw-confirm-images" aria-label="Photos to add">
          {preview.images.map((image) => (
            <li key={image.src}>
              <a href={image.src} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" title={`${image.alt} (opens full size)`}>
                <img src={image.src} alt={image.alt} referrerPolicy="no-referrer" loading="lazy" />
              </a>
              <span>{image.alt}</span>
            </li>
          ))}
        </ul>
      );
  }
}

/**
 * A change Stax won't make until the owner says so. The chat waits here: nothing else can be sent
 * until one of the two buttons is pressed.
 */
export function ConfirmationCard({ chat }: ConfirmationCardProps) {
  const { confirmation, deciding, decisionFailed, decide, connection } = chat;
  if (!confirmation) return null;
  const offline = connection === "reconnecting";
  const busy = deciding !== null;
  const { preview, note } = confirmation;
  const working = workingLabel(preview);
  const danger = preview?.kind === "delete" || (preview?.kind === "shop" && preview.action.endsWith("delete")) || (preview?.kind === "post" && (preview.action === "delete" || preview.action === "unpublish"));

  return (
    <div className={buyerDataClassName("bw-confirm-wrap", preview?.kind === "order")}>
      <p className="bw-confirm-question" id="bw-confirm-title">{confirmation.title}</p>
      <section className="bw-confirm" data-offline={offline || undefined} data-compact={!preview || undefined} role="group" aria-labelledby="bw-confirm-title" aria-busy={busy}>
        {preview && <Preview preview={preview} />}
        {decisionFailed && (
          <p className="bw-confirm-failed" role="alert"><CircleAlert size={16} aria-hidden="true" />Your answer didn’t reach Stax, so nothing changed. Choose again.</p>
        )}
        {confirmation.alternatives?.map(choice => <div key={choice.id} className="bw-confirm-alternative"><strong>{choice.title}</strong><p>{choice.detail}</p><button type="button" className="bw-btn bw-btn--danger" disabled={busy || offline} onClick={() => void decide("confirmed", choice.id)}>{choice.label}</button></div>)}
        <div className="bw-actions">
          <button type="button" className={`bw-btn ${danger ? "bw-btn--danger" : "bw-btn--primary"}`} disabled={busy || offline} onClick={() => void decide("confirmed")}>
            {deciding === "confirmed" ? <><Spinner />{working}</> : confirmation.confirmLabel}
          </button>
          <button type="button" className="bw-btn bw-btn--quiet" disabled={busy || offline} onClick={() => void decide("cancelled")}>
            {deciding === "cancelled" ? <><Spinner />{working}</> : confirmation.cancelLabel}
          </button>
        </div>
        {offline ? (
          <p className="bw-confirm-note" role="status"><Spinner />Reconnecting. You can answer in a moment.</p>
        ) : note ? <p className="bw-confirm-note">{note}</p> : null}
      </section>
    </div>
  );
}

/** The owner's answer, as the strip that stands in for their message once a question is answered. */
export function ConfirmationAnswer({ card }: { card: Extract<MessageCard, { kind: "confirmation-answered" }> }) {
  const confirmed = card.decision === "confirmed";
  return (
    <li className={buyerDataClassName("bw-answer", card.buyerData === true)} data-decision={card.decision}>
      <span className="bw-sr">You: </span>
      {confirmed ? <Check size={18} aria-hidden="true" /> : <X size={16} aria-hidden="true" />}
      <span>{card.text}</span>
    </li>
  );
}
